import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { Button, FileUploader } from '@unified-checkout/design-system';
import { Download } from '@unified-checkout/design-system/icons/index';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { TRowsToColumnsResult } from '@app/dashboard/types/innerLayout/excel-file-parser';
import { REACH_PAGE_EVENTS } from '@app/dashboard/constants/tracking_events/reach';
import { useLangStore } from '@app/dashboard/store/lang';
import { getPaymobAppServerUrl } from '@app/dashboard/utils/serverUrls';
import { parseExcelFile } from '@app/dashboard/utils/getDataFromExcel';
import {
  ALLOWED_FILE_TYPES,
  CREATE_CAMPAIGN_FIELDS,
  EGY_PHONE_REGEX,
  MAX_ROWS_IN_FILE,
  NO_ROWS_IN_FILE,
  PHONE_CELL,
  TEMPLATE_DOWNLOAD_ENDPOINT,
} from '@app/dashboard/constants/reach/modals/create-campaign';

import styles from './UploadPhoneNumbers.module.scss';

const UploadPhoneNumbers = ({
  fields,
  setFields,
  availableAmount,
  isModalOpen,
  isCreateLoading,
}: {
  fields: IField;
  setFields: Function;
  availableAmount: number;
  isModalOpen: boolean;
  isCreateLoading: boolean;
}) => {
  const [fileName, setFileName] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const phoneNumbers = fields[CREATE_CAMPAIGN_FIELDS.MOBILE_NUMBERS];

  const handleDownloadTemplate = () => {
    window.open(getPaymobAppServerUrl() + TEMPLATE_DOWNLOAD_ENDPOINT, '_blank');
    amplitude.track(REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.TEMPLATE_DOWNLOADED);
  };

  const updatePhoneNumbersField = (data: any) => {
    setFields((prevFields: any) => ({
      ...prevFields,
      [CREATE_CAMPAIGN_FIELDS.MOBILE_NUMBERS]: {
        ...prevFields[CREATE_CAMPAIGN_FIELDS.MOBILE_NUMBERS],
        ...data,
      },
    }));
  };

  const setFileErrors = (errorMsg: string) => {
    updatePhoneNumbersField({
      value: null,
      errorMsg,
      isValid: false,
    });
  };

  const validateFileData = (result: TRowsToColumnsResult): boolean => {
    let isValid = true;
    const { values, valuesWithMeta } = result || {};
    const phoneNumbers = values[PHONE_CELL] || [];

    if (!(PHONE_CELL in values)) {
      setFileErrors(
        t(
          'innerLayout.reach.campaigns.createCampaign.validations.invalidTemplate'
        )
      );

      isValid = false;
    } else if (phoneNumbers.length === NO_ROWS_IN_FILE) {
      setFileErrors(
        t('innerLayout.reach.campaigns.createCampaign.validations.emptySheet')
      );

      isValid = false;
    } else if (phoneNumbers.length === MAX_ROWS_IN_FILE) {
      setFileErrors(
        t('innerLayout.reach.campaigns.createCampaign.validations.largeData')
      );

      isValid = false;
    } else if (phoneNumbers.length > availableAmount) {
      setFileErrors(
        t('innerLayout.reach.campaigns.createCampaign.validations.planError')
      );

      isValid = false;
    } else {
      let duplicateHashMap = new Map();
      const phoneNumbers = valuesWithMeta[PHONE_CELL] || [];

      for (let index = 0; index < phoneNumbers.length; index++) {
        const phoneNumber =
          phoneNumbers[index]?.value?.toString()?.trim() || '';
        const rowNum = phoneNumbers[index]?.rowKey || 0;

        if (!phoneNumber.match(EGY_PHONE_REGEX)) {
          //check if EGY phone number is valid
          setFileErrors(
            t(
              'innerLayout.reach.campaigns.createCampaign.validations.phoneUnValidErrMsg',
              { row: rowNum }
            )
          );
          isValid = false;
          break;
        }

        if (duplicateHashMap.has(phoneNumber)) {
          // check if the number is duplicated
          setFileErrors(
            t(
              'innerLayout.reach.campaigns.createCampaign.validations.duplicateNumber',
              { row: rowNum, number: phoneNumber }
            )
          );
          isValid = false;
          break;
        }
        duplicateHashMap.set(phoneNumber, 0);
      }
    }

    return isValid;
  };

  const handleParseFile = async (file?: File) => {
    if (file) {
      setFileErrors('');

      setFileName(file.name);
      const results = (await parseExcelFile(file)) as TRowsToColumnsResult;

      let isValid = validateFileData(results);

      if (isValid) {
        updatePhoneNumbersField({
          value: results.values[PHONE_CELL] || [],
          isValid: true,
          errorMsg: '',
        });
      }
    } else {
      setFileErrors(t('errors.required'));
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      setResetKey((prev) => prev + 1);
      setFileName('');
    }
  }, [isModalOpen]);

  return (
    <div className={styles['reach-create-campaign__body-upload']}>
      <FileUploader
        label={t(
          'innerLayout.reach.campaigns.createCampaign.labels.uploadCampaignRecipients'
        )}
        allowedFormats={ALLOWED_FILE_TYPES}
        allowedSize={5}
        onUploadHandler={handleParseFile as any}
        error={phoneNumbers.errorMsg}
        previouslyUploadedFile={fileName}
        lang={lang}
        disabled={isCreateLoading}
        key={resetKey}
      />
      <Button
        text={t(
          'innerLayout.reach.campaigns.createCampaign.actions.downloadTemp'
        )}
        variant="link"
        trailingIcon={Download}
        size="sm"
        onClickHandler={handleDownloadTemplate}
        disabled={isCreateLoading}
      />
    </div>
  );
};

export default UploadPhoneNumbers;
